import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import AppDateInput from '../../../components/AppDateInput'
import AppDropdown from '../../../components/AppDropdown'
import EntityMultiSelectPanel from '../../../components/EntityMultiSelectPanel'
import { useUserEntryScreen } from './hooks/useUserEntryScreen'
import { appToast } from '../../../components/AppToast'
import { useAuth } from '../../../hooks/useAuth'
import ChangePasswordModal from './components/ChangePasswordModal'
import MasterApprovalBanner from '../../approval/components/MasterApprovalBanner'

function initialsFromName(name) {
  return String(name || 'NA')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

function formatCurrency(value) {
  return Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export default function UserProfileManagementScreen() {
  const { state, actions } = useUserEntryScreen()
  const { hasPrivilege, isAdmin } = useAuth()
  const canViewCtc = isAdmin || hasPrivilege('UserFinancialsView')
  const canEditCtc = isAdmin || hasPrivilege('UserFinancialsEdit')

  useEffect(() => {
    if (state.error) {
      appToast.error(state.error)
    }
  }, [state.error])

  useEffect(() => {
    if (state.success) {
      appToast.success(state.success)
    }
  }, [state.success])

  const [isAdditionalCostPickerOpen, setIsAdditionalCostPickerOpen] = useState(false)
  const [additionalCostSearch, setAdditionalCostSearch] = useState('')
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false)

  const filteredAdditionalCosts = useMemo(() => {
    const query = additionalCostSearch.trim().toLowerCase()
    if (!query) return state.normalizedAdditionalCostOptions
    return state.normalizedAdditionalCostOptions.filter((item) => {
      const reason = String(item.reason || '').toLowerCase()
      const period = String(item.costPeriod || '').toLowerCase()
      return reason.includes(query) || period.includes(query)
    })
  }, [additionalCostSearch, state.normalizedAdditionalCostOptions])

  return (
    <div className="mx-auto flex w-full max-w-[1550px] flex-col gap-8 pb-12">
      {/* Header Bar */}
      <section className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-on-surface-variant">Employee Directory</p>
          <h1 className="mt-1 text-4xl font-extrabold tracking-[-0.02em] text-on-surface">
            {state.form.name || (state.isEditMode ? 'User Profile' : 'Create User')}
          </h1>
          <p className="mt-2 text-base text-on-surface-variant">
            {state.isEditMode
              ? 'Manage identity access, multi-role security, and organization hierarchy mapping for this user.'
              : 'Create and configure a new enterprise identity profile with multi-role and organization mapping.'}
          </p>
        </div>

        {/* <div className="flex flex-wrap items-center gap-3">
          {state.isEditMode ? (
            <>
              <button
                type="button"
                onClick={actions.handleResetPassword}
                disabled={state.isSaving || state.isLoading}
                className="inline-flex items-center gap-2 rounded-md border border-outline-variant/30 bg-surface-container-lowest px-6 py-3 text-sm font-semibold text-on-surface disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="material-symbols-outlined text-base">password</span>
                Reset Password
              </button>
              <button
                type="button"
                onClick={actions.handleArchive}
                disabled={state.isSaving || state.isLoading}
                className="inline-flex items-center gap-2 rounded-md bg-error-container px-6 py-3 text-sm font-semibold text-error disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="material-symbols-outlined text-base">block</span>
                Deactivate
              </button>
            </>
          ) : null}
        </div> */}
      </section>

      <MasterApprovalBanner pendingApproval={state.pendingApproval} queueRoute="/approval/masters/list?module=users" />

      {state.isLoading ? (
        <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest px-6 py-20 text-center text-sm text-on-surface-variant">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          Loading user profile...
        </div>
      ) : (
        <>
          {/* ── Row 1: Identity & Photo, Personal Details, Financials ───────── */}
          <section className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            {/* Card 1: Avatar & Security Credentials (approx 25-30% width) */}
            <div className="lg:col-span-4 xl:col-span-3 space-y-6">
              <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-lowest p-6">
                <div className="flex flex-col items-center">
                  <label className="group cursor-pointer" title="Click to change profile photo">
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      className="hidden"
                      onChange={(event) => actions.handlePhotoSelect(event.target.files?.[0] || null)}
                    />

                    {state.profilePhotoPreview ? (
                      <img
                        src={state.profilePhotoPreview}
                        alt={state.form.name || 'User'}
                        className="h-28 w-28 rounded-full border-4 border-white object-cover shadow-xl transition-transform group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-28 w-28 items-center justify-center rounded-full bg-primary-fixed text-4xl font-bold text-primary shadow-xl transition-transform group-hover:scale-105">
                        {initialsFromName(state.form.name)}
                      </div>
                    )}
                  </label>

                  <p className="mt-4 text-base font-black text-primary">
                    USER ID: {state.userCode || 'Auto on create'}
                  </p>

                  <div className="mt-2.5 flex flex-wrap items-center justify-center gap-2">
                    <span
                      className={`rounded-full px-3 py-0.5 text-xs font-bold ${state.form.is_archive ? 'bg-surface-container-high text-outline' : 'bg-emerald-100 text-emerald-700'
                        }`}
                    >
                      {state.form.is_archive ? 'INACTIVE' : 'ACTIVE'}
                    </span>
                    <span className="rounded-full bg-secondary-fixed px-3 py-0.5 text-xs font-bold text-on-secondary-container">
                      {state.form.bruno_access ? 'ADMIN ACCESS' : 'STANDARD ACCESS'}
                    </span>
                  </div>
                </div>

                <div className="mt-6 rounded-xl bg-surface-container-low p-3.5 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-on-surface">Login Access</p>
                      <p className="text-[10px] text-on-surface-variant">Allow user to sign in</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => actions.patchForm('is_archive', !state.form.is_archive)}
                      className={`relative h-6 w-11 rounded-full px-0.5 transition-colors ${state.form.is_archive ? 'bg-outline-variant' : 'bg-primary'
                        }`}
                      aria-label="Toggle login access"
                    >
                      <span
                        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${state.form.is_archive ? 'left-0.5' : 'left-5'
                          }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-outline-variant/15">
                    <div>
                      <p className="text-xs font-semibold text-on-surface">Mobile Access</p>
                      <p className="text-[10px] text-on-surface-variant">Enable on mobile app</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => actions.patchForm('is_mobileAccess', !state.form.is_mobileAccess)}
                      className={`relative h-6 w-11 rounded-full px-0.5 transition-colors ${state.form.is_mobileAccess ? 'bg-primary' : 'bg-outline-variant'
                        }`}
                      aria-label="Toggle mobile access"
                    >
                      <span
                        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${state.form.is_mobileAccess ? 'left-5' : 'left-0.5'
                          }`}
                      />
                    </button>
                  </div>

                  {state.isEditMode && state.form.email ? (
                    <div className="pt-2 border-t border-outline-variant/15">
                      <button
                        type="button"
                        onClick={() => setIsChangePasswordOpen(true)}
                        className="flex w-full items-center justify-center gap-2 rounded-lg border border-outline-variant/30 bg-surface px-3 py-2 text-xs font-semibold text-on-surface hover:bg-surface-container-high hover:border-primary/40 transition-colors shadow-sm"
                      >
                        <span className="material-symbols-outlined text-base text-primary">lock_reset</span>
                        Change Password
                      </button>
                    </div>
                  ) : null}
                </div>
              </article>
            </div>

            {/* Card 2: Personal & Employment Details (Flexible center, approx 50% width) */}
            <div className="lg:col-span-8 xl:col-span-6 space-y-6">
              <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-lowest p-6 lg:p-7">
                <div className="mb-5 flex items-center gap-2.5">
                  <span className="material-symbols-outlined rounded-md bg-secondary-fixed p-1.5 text-primary text-xl">badge</span>
                  <h2 className="text-2xl font-bold tracking-[-0.02em] text-on-surface">Personal &amp; Employment Details</h2>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <label className="grid gap-1.5">
                    <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                      Full Name <span className="text-error">*</span>
                    </span>
                    <input
                      type="text"
                      value={state.form.name}
                      onChange={(event) => actions.patchForm('name', event.target.value)}
                      placeholder="Enter full name"
                      className="w-full rounded-md border-0 bg-surface-container-low px-3.5 py-2.5 text-sm text-on-surface outline-none focus:ring-1 focus:ring-primary"
                    />
                  </label>

                  <label className="grid gap-1.5">
                    <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                      Work Email <span className="text-error">*</span>
                    </span>
                    <input
                      type="email"
                      value={state.form.email}
                      onChange={(event) => actions.patchForm('email', event.target.value)}
                      disabled={state.isEditMode}
                      placeholder="name@company.com"
                      className="w-full rounded-md border-0 bg-surface-container-low px-3.5 py-2.5 text-sm text-on-surface outline-none disabled:cursor-not-allowed disabled:opacity-60 focus:ring-1 focus:ring-primary"
                    />
                  </label>

                  <label className="grid gap-1.5">
                    <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Phone Number</span>
                    <input
                      type="text"
                      value={state.form.phone}
                      onChange={(event) => actions.patchForm('phone', event.target.value)}
                      placeholder="+1 (555) 012-3456"
                      className="w-full rounded-md border-0 bg-surface-container-low px-3.5 py-2.5 text-sm text-on-surface outline-none focus:ring-1 focus:ring-primary"
                    />
                  </label>

                  <label className="grid gap-1.5">
                    <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Designation</span>
                    <input
                      type="text"
                      value={state.form.designation}
                      onChange={(event) => actions.patchForm('designation', event.target.value)}
                      placeholder="e.g. Senior ERP Consultant"
                      className="w-full rounded-md border-0 bg-surface-container-low px-3.5 py-2.5 text-sm text-on-surface outline-none focus:ring-1 focus:ring-primary"
                    />
                  </label>

                  <label className="grid gap-1.5">
                    <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Reporting Manager</span>
                    <AppDropdown
                      value={state.form.reporting_manager_id}
                      onChange={(value) => actions.patchForm('reporting_manager_id', value)}
                      fetchOptions={actions.fetchManagerOptions}
                      placeholder="Select Manager"
                      triggerClassName="w-full rounded-md border-0 bg-surface-container-low px-3.5 py-2.5 text-sm text-on-surface disabled:cursor-not-allowed disabled:opacity-60"
                      initialLabel={state.initialLabels.reporting_manager_name}
                    />
                  </label>

                  <label className="grid gap-1.5">
                    <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Work Shift</span>
                    <AppDropdown
                      value={state.form.shift_master_id}
                      onChange={(value) => actions.patchForm('shift_master_id', value)}
                      fetchOptions={actions.fetchShiftOptions}
                      placeholder="Select Shift"
                      triggerClassName="w-full rounded-md border-0 bg-surface-container-low px-3.5 py-2.5 text-sm text-on-surface disabled:cursor-not-allowed disabled:opacity-60"
                      initialLabel={state.initialLabels.shift_master_name}
                    />
                  </label>

                  <label className="grid gap-1.5">
                    <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Employee Group</span>
                    <AppDropdown
                      value={state.form.employee_group_id}
                      onChange={(value) => actions.patchForm('employee_group_id', value)}
                      fetchOptions={actions.fetchEmployeeGroupOptions}
                      placeholder="Select Employee Group"
                      triggerClassName="w-full rounded-md border-0 bg-surface-container-low px-3.5 py-2.5 text-sm text-on-surface disabled:cursor-not-allowed disabled:opacity-60"
                      initialLabel={state.initialLabels.employee_group_name}
                    />
                  </label>

                  <label className="grid gap-1.5">
                    <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">ERP Employee ID</span>
                    <input
                      type="number"
                      value={state.form.erp_employee_id}
                      onChange={(event) => actions.patchForm('erp_employee_id', event.target.value)}
                      placeholder="Optional ERP ID"
                      className="w-full rounded-md border-0 bg-surface-container-low px-3.5 py-2.5 text-sm text-on-surface outline-none focus:ring-1 focus:ring-primary"
                    />
                  </label>

                  <label className="grid gap-1.5">
                    <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Joining Date</span>
                    <AppDateInput
                      value={state.form.joining_date}
                      onChange={(value) => actions.patchForm('joining_date', value)}
                      inputClassName="w-full rounded-md border-0 bg-surface-container-low px-3.5 py-2.5 text-sm text-on-surface shadow-none"
                    />
                  </label>
                </div>
              </article>
            </div>

            {/* Card 3: Financials & Working Hours (approx 20-25% width) */}
            <div className="lg:col-span-12 xl:col-span-3 space-y-6">
              <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-lowest p-6">
                <div className="mb-4 flex items-center gap-2.5">
                  <span className="material-symbols-outlined rounded-md bg-secondary-fixed p-1.5 text-primary text-xl">payments</span>
                  <h2 className="text-xl font-bold tracking-[-0.02em] text-on-surface">Financials &amp; Hours</h2>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 xl:grid-cols-1">
                  {canViewCtc && (
                    <label className="grid gap-1">
                      <span className="text-[10px] font-black uppercase tracking-[0.12em] text-on-surface-variant">CTC Per Hr</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        disabled={!canEditCtc}
                        value={state.form.CTCperHr ?? ''}
                        onChange={(event) => actions.patchForm('CTCperHr', event.target.value)}
                        placeholder={canEditCtc ? 'e.g. 550.00' : 'Restricted'}
                        className={`w-full rounded-md border-0 bg-surface-container-low px-3 py-2 text-xs text-on-surface outline-none focus:ring-1 focus:ring-primary ${
                          !canEditCtc ? 'opacity-60 cursor-not-allowed' : ''
                        }`}
                      />
                    </label>
                  )}

                  <label className="grid gap-1">
                    <span className="text-[10px] font-black uppercase tracking-[0.12em] text-on-surface-variant">Full Day Hours</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={state.form.productive_hours_per_day}
                      onChange={(event) => actions.patchForm('productive_hours_per_day', event.target.value)}
                      placeholder="7.00"
                      className="w-full rounded-md border-0 bg-surface-container-low px-3 py-2 text-xs text-on-surface outline-none focus:ring-1 focus:ring-primary"
                    />
                  </label>

                  <label className="grid gap-1">
                    <span className="text-[10px] font-black uppercase tracking-[0.12em] text-on-surface-variant">Half Day Hours</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={state.form.productive_hours_half_day}
                      onChange={(event) => actions.patchForm('productive_hours_half_day', event.target.value)}
                      placeholder="5.00"
                      className="w-full rounded-md border-0 bg-surface-container-low px-3 py-2 text-xs text-on-surface outline-none focus:ring-1 focus:ring-primary"
                    />
                  </label>
                </div>

                <div className="mt-4 pt-3.5 border-t border-outline-variant/15">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.12em] text-secondary">Additional Costs</p>
                      <p className="text-2xl font-black tracking-tight text-on-surface mt-0.5">
                        {formatCurrency(state.selectedAdditionalCostMonthlyTotal)}
                      </p>
                      <p className="text-[10px] text-on-surface-variant">{state.selectedAdditionalCostCount} rules active</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsAdditionalCostPickerOpen(true)}
                      className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-dashed border-outline-variant text-on-surface-variant transition-all hover:border-primary hover:text-primary"
                      aria-label="Add additional costs"
                    >
                      <span className="material-symbols-outlined text-lg">add</span>
                    </button>
                  </div>
                </div>
              </article>
            </div>
          </section>

          {/* ── Row 2: Enterprise Access & Organization Hierarchy (100% Full Width) ── */}
          <section className="w-full">
            <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-lowest p-6 lg:p-8">
              <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined rounded-xl bg-secondary-fixed p-2.5 text-primary text-2xl">
                    admin_panel_settings
                  </span>
                  <div>
                    <h2 className="text-2xl font-bold tracking-[-0.02em] text-on-surface">
                      Enterprise Access &amp; Organization Mapping
                    </h2>
                    <p className="mt-0.5 text-xs text-on-surface-variant">
                      Assign multi-role security credentials and map enterprise company, branch, and department access with strict cascading and auto-pruning.
                    </p>
                  </div>
                </div>
              </div>

              {/* Data Scope / Record-Level Access Selector */}
              <div className="mb-7 rounded-2xl border border-outline-variant/20 bg-surface-container-low/20 p-5">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3.5">
                  <div>
                    <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant block">
                      Record-Level Data Scope
                    </span>
                    <p className="text-xs text-on-surface-variant/80 mt-0.5">
                      Controls the boundary of records this user is authorized to view, create, and manage across the system.
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-container-high px-3 py-1 text-[11px] font-semibold text-on-surface-variant">
                    <span className="material-symbols-outlined text-[15px] text-primary">security</span>
                    User Level Scope
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
                  {[
                    {
                      value: 'SELF',
                      label: 'Self Scope',
                      tag: 'Individual Records',
                      icon: 'person',
                      hint: 'Restricted strictly to personal tasks, assigned jobs, and user-created records.',
                    },
                    {
                      value: 'TEAM',
                      label: 'Team Scope',
                      tag: 'Reporting Hierarchy',
                      icon: 'diversity_3',
                      hint: 'Extends to direct and indirect reportees down the managerial reporting tree.',
                    },
                    {
                      value: 'ALL',
                      label: 'All Organization',
                      tag: 'Full Visibility',
                      icon: 'domain_verification',
                      hint: 'Full visibility across all tenant company projects, department tasks, and team records.',
                    },
                  ].map((option) => {
                    const selected = (state.form.data_scope || 'SELF') === option.value
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => actions.patchForm('data_scope', option.value)}
                        className={`group relative rounded-xl border p-4 text-left transition-all duration-200 cursor-pointer ${
                          selected
                            ? 'border-primary/70 bg-primary/[0.08] shadow-sm ring-1 ring-primary/30'
                            : 'border-outline-variant/30 bg-surface-container-lowest hover:border-outline-variant/60 hover:bg-surface-container-low/50'
                        }`}
                        role="radio"
                        aria-checked={selected}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2.5">
                            <div
                              className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
                                selected
                                  ? 'bg-primary text-on-primary shadow-xs'
                                  : 'bg-surface-container-high text-on-surface-variant group-hover:text-primary'
                              }`}
                            >
                              <span className="material-symbols-outlined text-lg">{option.icon}</span>
                            </div>
                            <div>
                              <p className={`text-sm font-bold leading-tight ${selected ? 'text-primary' : 'text-on-surface'}`}>
                                {option.label}
                              </p>
                              <span className="inline-block text-[10px] font-semibold uppercase tracking-wider text-on-surface-variant/70 mt-0.5">
                                {option.tag}
                              </span>
                            </div>
                          </div>
                          <div
                            className={`flex h-5 w-5 items-center justify-center rounded-full border transition-all ${
                              selected
                                ? 'border-primary bg-primary text-on-primary'
                                : 'border-outline-variant/40 bg-surface-container-lowest'
                            }`}
                          >
                            {selected ? (
                              <span className="material-symbols-outlined text-[13px] font-bold">check</span>
                            ) : null}
                          </div>
                        </div>
                        <p className="text-xs text-on-surface-variant mt-2.5 leading-relaxed">
                          {option.hint}
                        </p>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Entity Access Control Selector */}
              <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-low/40 p-4 sm:p-5">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3.5">
                  <div>
                    <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant block">
                      Entity & Organization Boundary
                    </span>
                    <p className="text-xs text-on-surface-variant/80 mt-0.5">
                      Choose whether this user has unrestricted access across all tenant entities or is strictly restricted to assigned entities.
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-container-high px-3 py-1 text-[11px] font-semibold text-on-surface-variant">
                    <span className="material-symbols-outlined text-[15px] text-primary">domain</span>
                    Access Boundary
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                  {[
                    {
                      value: 'ALL',
                      label: 'All Entities (Full Organization)',
                      tag: 'Tenant-Wide Access',
                      icon: 'domain_add',
                      hint: 'Automatically grants access to all current and future Companies, Branches, and Departments within this tenant. Ideal for Executives and Admins.',
                    },
                    {
                      value: 'SELECTED',
                      label: 'Selected Entities Only',
                      tag: 'Explicit Boundary',
                      icon: 'checklist_rtl',
                      hint: 'Restricts user access strictly to the Companies, Branches, and Departments explicitly chosen in the panels below.',
                    },
                  ].map((option) => {
                    const selected = (state.form.entity_access || 'SELECTED') === option.value
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => actions.patchForm('entity_access', option.value)}
                        className={`group relative rounded-xl border p-4 text-left transition-all duration-200 cursor-pointer ${
                          selected
                            ? 'border-primary/70 bg-primary/[0.08] shadow-sm ring-1 ring-primary/30'
                            : 'border-outline-variant/30 bg-surface-container-lowest hover:border-outline-variant/60 hover:bg-surface-container-low/50'
                        }`}
                        role="radio"
                        aria-checked={selected}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2.5">
                            <div
                              className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
                                selected
                                  ? 'bg-primary text-on-primary shadow-xs'
                                  : 'bg-surface-container-high text-on-surface-variant group-hover:text-primary'
                              }`}
                            >
                              <span className="material-symbols-outlined text-lg">{option.icon}</span>
                            </div>
                            <div>
                              <p className={`text-sm font-bold leading-tight ${selected ? 'text-primary' : 'text-on-surface'}`}>
                                {option.label}
                              </p>
                              <span className="inline-block text-[10px] font-semibold uppercase tracking-wider text-on-surface-variant/70 mt-0.5">
                                {option.tag}
                              </span>
                            </div>
                          </div>
                          <div
                            className={`flex h-5 w-5 items-center justify-center rounded-full border transition-all ${
                              selected
                                ? 'border-primary bg-primary text-on-primary'
                                : 'border-outline-variant/40 bg-surface-container-lowest'
                            }`}
                          >
                            {selected ? (
                              <span className="material-symbols-outlined text-[13px] font-bold">check</span>
                            ) : null}
                          </div>
                        </div>
                        <p className="text-xs text-on-surface-variant mt-2.5 leading-relaxed">
                          {option.hint}
                        </p>
                      </button>
                    )
                  })}
                </div>
              </div>

              {state.form.entity_access === 'ALL' ? (
                <div className="flex items-center gap-2.5 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-xs text-primary">
                  <span className="material-symbols-outlined text-lg flex-shrink-0">info</span>
                  <p>
                    <strong className="font-semibold">All Entities Mode Active:</strong> This user automatically has full access to all tenant Companies, Branches, and Departments. Specific entity selections below are optional and serve as primary defaults.
                  </p>
                </div>
              ) : null}

              {/* Spacious 4-Column Responsive Grid */}
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
                {/* Panel 1: Roles (*) */}
                <EntityMultiSelectPanel
                  title="Roles"
                  icon="shield_person"
                  required
                  colorTheme="accent"
                  selectedIds={state.form.role_ids}
                  options={state.roleOptions}
                  onToggle={actions.toggleRole}
                  onSelectAll={actions.selectAllRoles}
                  onClearAll={actions.clearAllRoles}
                  searchPlaceholder="Search roles..."
                  emptyMessage="No active roles found"
                />

                {/* Panel 2: Companies (*) */}
                <EntityMultiSelectPanel
                  title="Companies"
                  icon="domain"
                  required={state.form.entity_access !== 'ALL'}
                  colorTheme="primary"
                  selectedIds={state.form.company_ids}
                  options={state.companyOptions}
                  onToggle={actions.toggleCompany}
                  onSelectAll={actions.selectAllCompanies}
                  onClearAll={actions.clearAllCompanies}
                  disabled={state.form.entity_access === 'ALL'}
                  disabledMessage="Full Organization: User has full access to all current and future companies"
                  searchPlaceholder="Search companies..."
                  emptyMessage="No active companies found"
                />

                {/* Panel 3: Branches (Cascaded) */}
                <EntityMultiSelectPanel
                  title="Branches"
                  icon="apartment"
                  colorTheme="secondary"
                  selectedIds={state.form.branch_ids}
                  options={state.branchOptions}
                  onToggle={actions.toggleBranch}
                  onSelectAll={actions.selectAllBranches}
                  onClearAll={actions.clearAllBranches}
                  disabled={state.form.entity_access === 'ALL' || state.form.company_ids.length === 0}
                  disabledMessage={
                    state.form.entity_access === 'ALL'
                      ? 'Full Organization: User has full access to all current and future branches'
                      : 'Select at least one company to view branches'
                  }
                  isLoading={state.isOrgOptionsLoading}
                  searchPlaceholder="Search branches..."
                  emptyMessage="No branches mapped to selected companies"
                />

                {/* Panel 4: Departments (Cascaded) */}
                <EntityMultiSelectPanel
                  title="Departments"
                  icon="corporate_fare"
                  colorTheme="tertiary"
                  selectedIds={state.form.department_ids}
                  options={state.departmentOptions}
                  onToggle={actions.toggleDepartment}
                  onSelectAll={actions.selectAllDepartments}
                  onClearAll={actions.clearAllDepartments}
                  disabled={state.form.entity_access === 'ALL' || state.form.company_ids.length === 0}
                  disabledMessage={
                    state.form.entity_access === 'ALL'
                      ? 'Full Organization: User has full access to all current and future departments'
                      : 'Select at least one company to view departments'
                  }
                  isLoading={state.isOrgOptionsLoading}
                  searchPlaceholder="Search departments..."
                  emptyMessage="No departments mapped in selected hierarchy"
                />
              </div>
            </article>
          </section>

          {/* ── Row 3: Onboarding Completion Progress Bar ─────────────────── */}
          <section className="rounded-[2rem] bg-primary/5 px-6 py-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-black uppercase tracking-[0.14em] text-primary">Profile Onboarding Completion</p>
              <p className="text-sm font-bold text-primary">{state.completionPercent}%</p>
            </div>
            <div className="mt-2.5 h-2 w-full rounded-full bg-surface-variant overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${Math.max(8, state.completionPercent)}%` }}
              />
            </div>
          </section>

          {/* ── Action Buttons ───────────────────────────────────────────── */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Link
              to={state.returnTo}
              className="inline-flex items-center justify-center rounded-md bg-surface-container-high px-8 py-3 text-sm font-semibold text-on-surface-variant transition-colors hover:bg-surface-container-highest"
            >
              Discard Changes
            </Link>
            <button
              type="button"
              onClick={actions.handleSave}
              disabled={state.isSaving || Boolean(state.pendingApproval)}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-9 py-3 text-sm font-bold text-on-primary shadow-lg shadow-primary/20 transition-all hover:bg-primary/95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="material-symbols-outlined text-base">save</span>
              {state.pendingApproval ? 'Submitted for Approval' : state.isSaving ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </>
      )}

      {/* Additional Costs Selection Modal */}
      {isAdditionalCostPickerOpen ? (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4 backdrop-blur-xs">
          <div className="w-full max-w-xl rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-5 shadow-2xl">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-lg font-bold text-on-surface">Add Additional Costs</h4>
              <button
                type="button"
                onClick={() => setIsAdditionalCostPickerOpen(false)}
                className="rounded-full p-1 hover:bg-surface-container-low transition-colors"
                aria-label="Close additional costs modal"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="relative mb-3">
              <span className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-outline">
                search
              </span>
              <input
                type="text"
                value={additionalCostSearch}
                onChange={(event) => setAdditionalCostSearch(event.target.value)}
                placeholder="Search additional costs..."
                className="w-full rounded-md border border-outline-variant/30 bg-surface-container-lowest py-2.5 pl-10 pr-4 text-sm text-on-surface outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>

            <div className="max-h-[360px] space-y-1 overflow-y-auto rounded-md border border-outline-variant/30 bg-surface-container-lowest p-1 pr-2">
              {filteredAdditionalCosts.length ? (
                filteredAdditionalCosts.map((item) => {
                  const selected = state.form.AdditionalCostCsv.includes(Number(item.id))
                  return (
                    <button
                      key={`additional-cost-${item.id}`}
                      type="button"
                      onClick={() => actions.toggleAdditionalCost(item.id)}
                      className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left transition-colors ${selected ? 'bg-primary-fixed text-primary' : 'hover:bg-surface-container-low'
                        }`}
                    >
                      <div>
                        <p className="text-sm font-semibold">{item.reason}</p>
                        <p className="text-xs opacity-80">
                          {formatCurrency(item.cost)} | {item.costPeriod} | Monthly {formatCurrency(item.monthlyEquivalent)}
                        </p>
                      </div>
                      <span className="text-xs font-semibold">{selected ? 'Selected' : 'Add'}</span>
                    </button>
                  )
                })
              ) : (
                <div className="px-3 py-4 text-center text-sm text-on-surface-variant">No additional costs found.</div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <ChangePasswordModal
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
        email={state.form.email}
        userName={state.form.name}
      />
    </div>
  )
}
